import type { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import type { Absence, Employee, PaycodeAssign, PaycodeDuration, Shift, Tag } from '../types/services';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { useTranslation } from 'react-i18next';
import { employeeService } from '../../employee/services/service';
import dayjs from 'dayjs';
import { serviceSchedule } from '../services/service';
import { useAuthorization } from '../../../../contexts/AuthorizationContext';
import { usePermisoLecturaSchedule } from '../../../../auth/permisosModulos';

const useToolbar = () => {
    const { showAlert } = useRbSystemAlert();
    const { t } = useTranslation('workers.table');
    const { authorization, hasTagAccess } = useAuthorization();

    const canViewShifts = usePermisoLecturaSchedule('shifts');
    const canViewAbsences = usePermisoLecturaSchedule('absences');
    const canViewPaycodeAssign = usePermisoLecturaSchedule('paycode_assigns');
    const canViewPaycodeDuration = usePermisoLecturaSchedule('duration_paycodes');
    const canViewTags = usePermisoLecturaSchedule('tags');
    const canViewEmployees = usePermisoLecturaSchedule('employees');
    // Sin tags en el token no se restringe, igual que en useFormAddTag.
    const restrictTags = (authorization?.tagIds.length ?? 0) > 0;

    const [period, setPeriod] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

    const [tags, setTags] = useState<Tag[]>([]);
    const [tagSelected, setTagSelected] = useState<string[]>([]);
    const [searchTag, setSearchTag] = useState<string>('');

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [employeeSelected, setEmployeeSelected] = useState<string[]>([]);
    const [searchEmployee, setSearchEmployee] = useState<string>('');

    const [paycodeAssigns, setPaycodeAssigns] = useState<PaycodeAssign[]>([]);
    const [paycodeDuration, setPaycodeDuration] = useState<PaycodeDuration[]>([]);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [absences, setAbsences] = useState<Absence[]>([]);

    const [loading, setLoading] = useState<boolean>(false);

    const initializeToolbar = async () => {
        setLoading(true);

        const searchParams = new URLSearchParams(window.location.search);

        const startDate = searchParams.get('start_date');
        const endDate = searchParams.get('end_date');
        const rawTagIds = searchParams.get('tags');
        const tagIds =
            restrictTags && rawTagIds
                ? rawTagIds
                      .split(',')
                      .filter((tagId) => hasTagAccess(tagId))
                      .join(',')
                : rawTagIds;
        const employeeIds = searchParams.get('employees');

        const { tags: fetchedTags } = canViewTags
            ? await employeeService.getTags({
                  params: {},
                  alert: { showAlert, t },
              })
            : { tags: [] as Tag[] };

        const tags = restrictTags ? fetchedTags.filter((tag) => hasTagAccess(tag.value)) : fetchedTags;

        setTags(tags);

        if (dayjs(startDate).isValid() && dayjs(endDate).isValid() && tagIds && employeeIds) {
            setPeriod([dayjs(startDate), dayjs(endDate)]);
            setTagSelected(tagIds.split(','));

            const { employees } = canViewEmployees
                ? await employeeService.getEmployees({
                      params: {
                          start_date: dayjs(startDate).format('YYYY-MM-DD'),
                          end_date: dayjs(endDate).format('YYYY-MM-DD'),
                          tags_id__in: tagIds || undefined,
                          active: true,
                      },
                      alert: { showAlert, t },
                  })
                : { employees: [] };

            setEmployees(
                employees.map((employee) => ({
                    label: `${employee.first_name} ${employee.last_name}`,
                    value: String(employee.employee_id),
                    sublabel: employee.phone_number,
                    timezone: employee.timezone,
                })),
            );

            setEmployeeSelected(employeeIds.split(','));

            await search(employeeIds, tagIds, dayjs(startDate).format('YYYY-MM-DD'), dayjs(endDate).format('YYYY-MM-DD'));
        } else {
            setPeriod([dayjs().startOf('month'), dayjs().endOf('month')]);
            setTagSelected(tags.map((tag) => tag.value.toString()));

            const { employees } = canViewEmployees
                ? await employeeService.getEmployees({
                      params: {
                          start_date: period[0]?.format('YYYY-MM-DD') || dayjs().startOf('month').format('YYYY-MM-DD'),
                          end_date: period[1]?.format('YYYY-MM-DD') || dayjs().endOf('month').format('YYYY-MM-DD'),
                          tags_id__in: tagIds || undefined,
                          active: true,
                      },
                      alert: { showAlert, t },
                  })
                : { employees: [] };

            setEmployees(
                employees.map((employee) => ({
                    label: `${employee.first_name} ${employee.last_name}`,
                    value: String(employee.employee_id),
                    sublabel: employee.phone_number,
                    timezone: employee.timezone,
                })),
            );

            setEmployeeSelected(employees.map((employee) => employee.employee_id.toString()));

            await search(
                employees.map((employee) => employee.employee_id.toString()).join(','),
                tagIds || '',
                period[0]?.format('YYYY-MM-DD') || dayjs().startOf('month').format('YYYY-MM-DD'),
                period[1]?.format('YYYY-MM-DD') || dayjs().endOf('month').format('YYYY-MM-DD'),
            );
        }

        setLoading(false);
    };

    const search = async (id_employee__in: string, id_tag__in: string, date__gte: string, date__lte: string) => {
        //search
        setLoading(true);

        const { paycodeAssigns } = canViewPaycodeAssign
            ? await serviceSchedule.getPaycodeAssign({
                  params: {
                      id_employee__in: id_employee__in,
                      date__gte: date__gte,
                      date__lte: date__lte,
                      template: 'Scheduler',
                  },
                  alert: { showAlert, t },
              })
            : { paycodeAssigns: [] };

        setPaycodeAssigns(paycodeAssigns);

        const { paycodeDuration } = canViewPaycodeDuration
            ? await serviceSchedule.getPaycodeDuration({
                  params: {
                      employee_id__in: id_employee__in,
                      date_timecard__gte: date__gte,
                      date_timecard__lte: date__lte,
                  },
                  alert: { showAlert, t },
              })
            : { paycodeDuration: [] };

        setPaycodeDuration(paycodeDuration);

        const { shifts } = canViewShifts
            ? await serviceSchedule.getShifts({
                  params: {
                      employee_id__in: id_employee__in,
                      start_date: date__gte,
                      end_date: date__lte,
                  },
                  alert: { showAlert, t },
              })
            : { shifts: [] };

        setShifts(shifts);

        const { absences } = canViewAbsences
            ? await serviceSchedule.getAbsences({
                  params: {
                      employee_id__in: id_employee__in,
                      start_date: date__gte,
                      end_date: date__lte,
                  },
                  alert: { showAlert, t },
              })
            : { absences: [] };

        setAbsences(absences);

        const params = new URLSearchParams({
            module: 'timekeeper',
            view: 'schedule',
            start_date: date__gte,
            end_date: date__lte,
            tags: id_tag__in,
            employees: id_employee__in,
        });

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, '', newUrl);

        setLoading(false);
    };

    const reload = async () => {
        setLoading(true);

        const searchParams = new URLSearchParams(window.location.search);

        const startDate = searchParams.get('start_date');
        const endDate = searchParams.get('end_date');
        const tagIds = searchParams.get('tags');
        const employeeIds = searchParams.get('employees');

        setLoading(true);

        const { paycodeAssigns } = canViewPaycodeAssign
            ? await serviceSchedule.getPaycodeAssign({
                  params: {
                      id_employee__in: employeeIds || '',
                      date__gte: startDate || '',
                      date__lte: endDate || '',
                      template: 'Scheduler',
                  },
                  alert: { showAlert, t },
              })
            : { paycodeAssigns: [] };

        setPaycodeAssigns(paycodeAssigns);

        const { paycodeDuration } = canViewPaycodeDuration
            ? await serviceSchedule.getPaycodeDuration({
                  params: {
                      employee_id__in: employeeIds || '',
                      date_timecard__gte: startDate || '',
                      date_timecard__lte: endDate || '',
                  },
                  alert: { showAlert, t },
              })
            : { paycodeDuration: [] };

        setPaycodeDuration(paycodeDuration);

        const { shifts } = canViewShifts
            ? await serviceSchedule.getShifts({
                  params: {
                      employee_id__in: employeeIds || '',
                      start_date: startDate || '',
                      end_date: endDate || '',
                  },
                  alert: { showAlert, t },
              })
            : { shifts: [] };

        setShifts(shifts);

        const { absences } = canViewAbsences
            ? await serviceSchedule.getAbsences({
                  params: {
                      employee_id__in: employeeIds || '',
                      start_date: startDate || '',
                      end_date: endDate || '',
                  },
                  alert: { showAlert, t },
              })
            : { absences: [] };

        setAbsences(absences);

        const params = new URLSearchParams({
            module: 'timekeeper',
            view: 'schedule',
            start_date: startDate || '',
            end_date: endDate || '',
            tags: tagIds || '',
            employees: employeeIds || '',
        });

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, '', newUrl);

        setLoading(false);
    };

    useEffect(() => {
        initializeToolbar();
    }, []);

    return {
        period,
        setPeriod,
        employees,
        setEmployees,
        employeeSelected,
        setEmployeeSelected,
        searchEmployee,
        setSearchEmployee,
        paycodeAssigns,
        setPaycodeAssigns,
        paycodeDuration,
        setPaycodeDuration,
        shifts,
        setShifts,
        absences,
        setAbsences,
        tags,
        setTags,
        tagSelected,
        setTagSelected,
        searchTag,
        setSearchTag,
        loading,
        setLoading,
        search,
        reload,
    };
};

export { useToolbar };
